import React, { useState, useEffect } from "react";
import AdminAppBody from "../../../../components/admin/AdminAppBody";
import PageHeading from "../../../../components/admin/common/PageHeading";
import useApiCallHooks from "../../../../hooks/useApiCallHooks";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { uiRoutes } from "../../../../routes/ui/uiRoutes";

const RoboticsKitEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    grades: "",
    age: "",
    image: null,
    level: "",
    projects: [],
    what_you_learn: [],
    price: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [response, loading, error, callAPI, statusCode] = useApiCallHooks();

  useEffect(() => {
    callAPI("get", `admin/robotic-kits/${id}`, "");
  }, [id]);

  useEffect(() => {
    if (response?.data && statusCode === 200) {
      const data = response.data;
      setFormData({
        name: data.name || "",
        description: data.description || "",
        grades: data.grades || "",
        age: data.age || "",
        image: null,
        level: data.level || "",
        projects: data.projects || [],
        what_you_learn: data.what_you_learn || [],
        price: data.price || "",
      });
      setImagePreview(data.image || null);
    }
  }, [response, statusCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "projects") {
        value.forEach((project, index) => {
          submitData.append(`projects[${index}][name]`, project.name);
          if (project.file) {
            submitData.append(`projects[${index}][file]`, project.file);
          }
        });
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          submitData.append(`${key}[${index}]`, item);
        });
      } else if (key === "image" && value) {
        submitData.append("image", value);
      } else {
        submitData.append(key, value);
      }
    });

    callAPI("post", `admin/robotic-kits/${id}?_method=PUT`, submitData);
  };

  useEffect(() => {
    if (statusCode === 202 && response?.data) {
      toast.success("Robotics Kit updated successfully!");
      setTimeout(() => navigate(uiRoutes.admin.roboticskit.list), 1000);
    }
  }, [response, statusCode, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, image: file });
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleProjectChange = (index, field, value) => {
    const updated = [...formData.projects];
    updated[index][field] = value;
    setFormData({ ...formData, projects: updated });
  };

  const addProject = () => {
    setFormData({ ...formData, projects: [...formData.projects, { name: "", file: null }] });
  };

  const removeProject = (index) => {
    const updated = [...formData.projects];
    updated.splice(index, 1);
    setFormData({ ...formData, projects: updated });
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeField = (field, index) => {
    const updated = [...formData[field]];
    updated.splice(index, 1);
    setFormData({ ...formData, [field]: updated });
  };

  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="container mt-5">
          <PageHeading title="Edit Robotics Kit" subTitle="Update kit details" />
          <div className="container card p-4 shadow-sm mt-3">
            <form onSubmit={handleSubmit} className="row g-3">
              <h6>Edit Robotics Kit Form</h6>

              {/* Name */}
              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <span className="text-danger">{error?.data?.name}</span>
              </div>

              {/* Grades */}
              <div className="col-md-6">
                <label className="form-label">Grades</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.grades}
                  onChange={(e) => setFormData({ ...formData, grades: e.target.value })}
                />
                <span className="text-danger">{error?.data?.grades}</span>
              </div>

              {/* Age */}
              <div className="col-md-6">
                <label className="form-label">Age</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                />
                <span className="text-danger">{error?.data?.age}</span>
              </div>

              {/* Level */}
              <div className="col-md-6">
                <label className="form-label">Level</label>
                <select
                  className="form-select"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                >
                  <option value="">Select Level</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advance">Advance</option>
                </select>
                <span className="text-danger">{error?.data?.level}</span>
              </div>

              {/* Image */}
              <div className="col-md-6">
                <label className="form-label">Image</label>
                <input type="file" accept="image/*" className="form-control" onChange={handleImageChange} />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mt-2"
                    style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
                  />
                )}
                <span className="text-danger">{error?.data?.image}</span>
              </div>

              {/* Price */}
              <div className="col-md-6">
                <label className="form-label">Price (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
                <span className="text-danger">{error?.data?.price}</span>
              </div>

              {/* Description */}
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                <span className="text-danger">{error?.data?.description}</span>
              </div>

              {/* Projects */}
              <div className="col-md-6">
                <label className="form-label">Projects</label>
                {formData.projects.map((project, index) => (
                  <div key={index} className="mb-2">
                    <input
                      type="text"
                      className="form-control mb-1"
                      placeholder="Project Name"
                      value={project.name}
                      onChange={(e) => handleProjectChange(index, "name", e.target.value)}
                    />
                    <input
                      type="file"
                      className="form-control mb-1"
                      onChange={(e) => handleProjectChange(index, "file", e.target.files[0])}
                    />
                    {formData.projects.length > 1 && (
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeProject(index)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addProject}>
                  + Add Project
                </button>
              </div>

              {/* What You Learn */}
              <div className="col-md-6">
                <label className="form-label">What You Learn</label>
                {formData.what_you_learn.map((item, index) => (
                  <div key={index} className="d-flex gap-2 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      value={item}
                      onChange={(e) => handleArrayChange("what_you_learn", index, e.target.value)}
                    />
                    {formData.what_you_learn.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeField("what_you_learn", index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => addField("what_you_learn")}
                >
                  + Add Learning Point
                </button>
              </div>

              {/* Submit */}
              <div className="col-md-4 mt-4 mb-4">
                <button type="submit" className="btn btn-primary btn-sm w-100">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    />
  );
};

export default RoboticsKitEdit;
